import { useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { submitHireRequest } from "@/lib/api";
import FadeUp from "@/components/landing/FadeUp";
import { FORM } from "./content";
import { scrollToId, slug } from "./utils";
import { ChipGroup, FieldBlock, Segmented, inputClass } from "./FormControls";
import { PrimaryButton } from "./Primitives";
import { FormSuccess } from "./FormSuccess";

const initialForm = {
    roles: [],
    employment_type: "",
    work_arrangement: "",
    location_preference: "",
    location_value: "",
    experience_level: "",
    engagement_duration: "",
    budget_amount: "",
    budget_currency: "USD",
    company: "",
    website: "",
    email: "",
    additional_requirements: "",
    package_interest: "",
};

const validate = (f) => {
    const e = {};
    if (!f.roles.length) e.roles = "Select at least one role.";
    if (!f.employment_type) e.employment_type = "Choose part-time or full-time.";
    if (!f.work_arrangement) e.work_arrangement = "Choose a work arrangement.";
    if (!f.location_preference) e.location_preference = "Choose a location preference.";
    else if (f.location_preference !== "anywhere" && !f.location_value.trim())
        e.location_preference = `Please enter the ${f.location_preference}.`;
    if (!f.experience_level) e.experience_level = "Choose an experience level.";
    if (!f.engagement_duration) e.engagement_duration = "Choose an engagement duration.";
    if (f.budget_amount !== "" && !(Number(f.budget_amount) > 0)) e.budget_amount = "Enter a budget greater than zero.";
    if (!f.company.trim()) e.company = "Company name is required.";
    if (!f.website.trim()) e.website = "Website is required.";
    else if (!/^(https?:\/\/)?[\w-]+(\.[\w-]+)+\S*$/i.test(f.website.trim()))
        e.website = "Enter a valid website URL.";
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) e.email = "Enter a valid work email.";
    return e;
};

export const HireForm = ({ preselectedPackage }) => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const formRef = useRef(null);

    const set = (k) => (v) => {
        const val = v?.target ? v.target.value : v;
        setForm((f) => ({ ...f, [k]: val }));
        setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
    };

    useEffect(() => {
        if (preselectedPackage?.name)
            setForm((f) => ({ ...f, package_interest: preselectedPackage.name }));
    }, [preselectedPackage]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        const errs = validate(form);
        setErrors(errs);
        const firstKey = Object.keys(errs)[0];
        if (firstKey) {
            formRef.current
                ?.querySelector(`[data-testid="field-${slug(firstKey)}"]`)
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error("Please complete the highlighted fields.");
            return;
        }
        setLoading(true);
        try {
            await submitHireRequest({
                ...form,
                budget_amount: form.budget_amount === "" ? null : Number(form.budget_amount),
                website: form.website.trim(),
                email: form.email.trim(),
                company: form.company.trim(),
                location_value: form.location_value.trim(),
            });
            setSuccess(true);
            setForm(initialForm);
            setTimeout(() => scrollToId("hire-form"), 60);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Something went wrong", {
                description:
                    typeof detail === "string"
                        ? detail
                        : "Please check your details and try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const locLabel = form.location_preference === "city" ? "City" : "Country";

    return (
        <section
            id="hire-form"
            data-testid="hire-form-section"
            className="section-pad bg-hire-bg border-y border-hire-line"
        >
            <div className="container-page">
                <div className="max-w-3xl mx-auto text-center">
                    <FadeUp>
                        <p className="text-xs font-semibold tracking-[0.14em] uppercase text-brand-blue">
                            Hiring requirements
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <h2
                            data-testid="hire-form-heading"
                            className="mt-3 font-bold text-[32px] leading-[1.15] sm:text-4xl md:text-[44px] text-hire-ink text-balance"
                        >
                            {FORM.heading}
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <p className="mt-5 text-base md:text-lg text-hire-muted leading-relaxed">
                            {FORM.description}
                        </p>
                    </FadeUp>
                </div>

                <FadeUp delay={0.15}>
                    {success ? (
                        <FormSuccess onReset={() => setSuccess(false)} />
                    ) : (
                        <form
                            ref={formRef}
                            noValidate
                            onSubmit={onSubmit}
                            data-testid="hire-form"
                            className="mt-12 md:mt-16 max-w-3xl mx-auto rounded-3xl border border-hire-line bg-white p-6 sm:p-8 md:p-12 shadow-[0_30px_80px_-50px_rgba(11,15,25,0.25)]"
                        >
                            <FieldBlock step="A" label="I want to hire" hint="Select all that apply" error={errors.roles} testId="field-roles">
                                <ChipGroup multiple name="roles" label="Roles" options={FORM.roles} value={form.roles} onChange={set("roles")} />
                            </FieldBlock>

                            <FieldBlock step="B" label="Employment type" error={errors.employment_type} testId="field-employment-type">
                                <Segmented name="employment" label="Employment type" options={FORM.employmentTypes} value={form.employment_type} onChange={set("employment_type")} />
                            </FieldBlock>

                            <FieldBlock step="C" label="Work arrangement" error={errors.work_arrangement} testId="field-work-arrangement">
                                <Segmented name="arrangement" label="Work arrangement" options={FORM.workArrangements} value={form.work_arrangement} onChange={set("work_arrangement")} />
                            </FieldBlock>

                            <FieldBlock step="D" label="Location preference" error={errors.location_preference} testId="field-location-preference">
                                <Segmented name="location" label="Location preference" options={FORM.locationPreferences} value={form.location_preference} onChange={(v) => { set("location_preference")(v); set("location_value")(""); }} />
                                {form.location_preference && form.location_preference !== "anywhere" && (
                                    <div className="mt-4 max-w-md">
                                        <label htmlFor="f-location" className="sr-only">{locLabel}</label>
                                        <input
                                            id="f-location"
                                            data-testid="input-location-value"
                                            autoFocus
                                            placeholder={form.location_preference === "city" ? "e.g. New York, London, Manila" : "e.g. United States, India, Philippines"}
                                            value={form.location_value}
                                            onChange={set("location_value")}
                                            className={inputClass(errors.location_preference)}
                                        />
                                    </div>
                                )}
                            </FieldBlock>

                            <FieldBlock step="E" label="Experience level" error={errors.experience_level} testId="field-experience-level">
                                <ChipGroup name="experience" label="Experience level" options={FORM.experienceLevels} value={form.experience_level} onChange={set("experience_level")} />
                            </FieldBlock>

                            <FieldBlock step="F" label="Engagement duration" error={errors.engagement_duration} testId="field-engagement-duration">
                                <Segmented name="duration" label="Engagement duration" options={FORM.engagementDurations} value={form.engagement_duration} onChange={set("engagement_duration")} />
                            </FieldBlock>

                            <FieldBlock step="G" label="Hiring budget" hint="Optional · monthly or total, your choice" htmlFor="f-budget" error={errors.budget_amount} testId="field-budget-amount">
                                <div className="flex gap-3 max-w-md">
                                    <div className="relative">
                                        <label htmlFor="f-currency" className="sr-only">Currency</label>
                                        <select
                                            id="f-currency"
                                            data-testid="select-currency"
                                            value={form.budget_currency}
                                            onChange={set("budget_currency")}
                                            className={cn(inputClass(false), "w-[104px] pr-8 appearance-none font-semibold")}
                                        >
                                            {FORM.currencies.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                        <span aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-hire-muted text-xs">▼</span>
                                    </div>
                                    <input
                                        id="f-budget"
                                        data-testid="input-budget-amount"
                                        type="number"
                                        inputMode="decimal"
                                        min="1"
                                        step="1"
                                        placeholder="e.g. 4000"
                                        value={form.budget_amount}
                                        onChange={set("budget_amount")}
                                        className={inputClass(errors.budget_amount)}
                                    />
                                </div>
                            </FieldBlock>

                            <FieldBlock step="H" label="Company name" htmlFor="f-company" error={errors.company} testId="field-company">
                                <input id="f-company" data-testid="input-company" placeholder="Your company" value={form.company} onChange={set("company")} className={`${inputClass(errors.company)} max-w-md`} />
                            </FieldBlock>

                            <FieldBlock step="I" label="Website" htmlFor="f-website" error={errors.website} testId="field-website">
                                <input id="f-website" data-testid="input-website" type="url" placeholder="https://" value={form.website} onChange={set("website")} className={`${inputClass(errors.website)} max-w-md`} />
                            </FieldBlock>

                            <FieldBlock step="J" label="Work email" htmlFor="f-email" error={errors.email} testId="field-email">
                                <input id="f-email" data-testid="input-email" type="email" autoComplete="email" placeholder="you@company.com" value={form.email} onChange={set("email")} className={`${inputClass(errors.email)} max-w-md`} />
                            </FieldBlock>

                            <FieldBlock step="K" label="Additional requirements" hint="Optional" htmlFor="f-notes" testId="field-additional-requirements">
                                <textarea
                                    id="f-notes"
                                    data-testid="input-additional-requirements"
                                    rows={4}
                                    placeholder="Tools they should know, markets you operate in, start date, anything else that matters."
                                    value={form.additional_requirements}
                                    onChange={set("additional_requirements")}
                                    className={cn(inputClass(false), "h-auto py-3 resize-none")}
                                />
                            </FieldBlock>

                            <FieldBlock label="Package you're interested in" hint="Optional" testId="field-package-interest">
                                <ChipGroup name="package" label="Package interest" options={FORM.packages} value={form.package_interest} onChange={(v) => set("package_interest")(v === form.package_interest ? "" : v)} />
                            </FieldBlock>

                            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                                <PrimaryButton type="submit" data-testid="hire-form-submit" disabled={loading} className="w-full sm:w-auto h-[52px] px-7">
                                    {loading ? (
                                        <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                                    ) : (
                                        <>{FORM.submit}<ArrowRight className="h-4 w-4" /></>
                                    )}
                                </PrimaryButton>
                                <p className="text-xs text-hire-muted leading-relaxed">
                                    No commitment. We&apos;ll reply to understand your needs before anything else.
                                </p>
                            </div>
                        </form>
                    )}
                </FadeUp>
            </div>
        </section>
    );
};

export default HireForm;
